import { OPTIMIZELY_EVENTS } from 'src/constants'

const options = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0,
}

interface ErrorProps {
  geolocationPositionError: GeolocationPositionError
  sendOptimizelyEvent: HandleNearbyPropertiesClickProps['sendOptimizelyEvent']
  warningLogger: HandleNearbyPropertiesClickProps['warningLogger']
}

function handleGeolocationErrors({
  geolocationPositionError,
  sendOptimizelyEvent,
  warningLogger,
}: ErrorProps) {
  switch (geolocationPositionError.code) {
    case geolocationPositionError.PERMISSION_DENIED:
      warningLogger('GD-482: User denied the request for Geolocation.')
      sendOptimizelyEvent(
        OPTIMIZELY_EVENTS.NEARBY_PROPERTIES_GEOLOCATION_ACCESS_DENIED,
      )
      break
    case geolocationPositionError.POSITION_UNAVAILABLE:
      warningLogger('GD-482: Location information is unavailable.')
      break
    case geolocationPositionError.TIMEOUT:
      warningLogger('GD-482: The request to get user location timed out.')
      break
    default:
      warningLogger(
        `GD-482: ERROR(${geolocationPositionError.code}): ${geolocationPositionError.message}`,
      )
  }

  if (
    geolocationPositionError.code !== geolocationPositionError.PERMISSION_DENIED
  ) {
    sendOptimizelyEvent(OPTIMIZELY_EVENTS.NEARBY_PROPERTIES_GEOLOCATION_ERROR)
  }

  return geolocationPositionError
}

interface HandleNearbyPropertiesClickProps {
  sendOptimizelyEvent: (eventKey: string) => void
  warningLogger: (message: string) => void
}

export const handleNearbyPropertiesClick = async ({
  sendOptimizelyEvent,
  warningLogger,
}: HandleNearbyPropertiesClickProps): Promise<GeolocationCoordinates> => {
  const browserWindow = globalThis.window

  if (!browserWindow) {
    throw new Error('Needs to be run in a browser')
  }

  if (!browserWindow.navigator?.geolocation) {
    warningLogger('GD-482: Geolocation is not supported by this browser.')

    throw new Error('Geolocation is not supported by this browser.')
  }

  return await new Promise<GeolocationCoordinates>((resolve, reject) => {
    browserWindow.navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve(coords),
      (error) => {
        const errorObject = handleGeolocationErrors({
          geolocationPositionError: error,
          sendOptimizelyEvent,
          warningLogger,
        })

        reject(new Error(errorObject.message, { cause: errorObject }))
      },
      options,
    )
  })
}
